import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import PromptVersionSelector from "@/components/ui/PromptVersionSelector";

import useIsMobile from "@/hooks/useIsMobile";
import { Transition } from "@headlessui/react";
import {
  FilterIcon as FilterIconOutline,
  XIcon,
} from "@heroicons/react/outline";
import { FilterIcon as FilterIconSolid } from "@heroicons/react/solid";
import { CheckIcon } from "lucide-react";
import { observer } from "mobx-react-lite";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import DatasetSelector from "./DatasetSelector";
import DateRangeSelector from "./DateRangeSelector";
import { useHistoryFilters } from "./history-filter-store";
import PipelineSelector from "./PipelineSelector";
import ReleaseLabelSelector from "./ReleaseLabelSelector";

const HistoryFilter = observer(() => {
  const [isOpen, setIsOpen] = useState(false);
  const filterStore = useHistoryFilters();
  const { promptId } = useParams();
  const isMobile = useIsMobile();
  const popoverRef = useRef<HTMLDivElement>(null);

  const numericPromptId = useMemo(
    () => (promptId ? Number(promptId) : undefined),
    [promptId],
  );

  const handleApply = useCallback(() => {
    filterStore.saveCurrentFilterAsPrevious();
    setIsOpen(false);
  }, [filterStore]);

  const handleCancel = useCallback(
    (closeDialog = true) => {
      filterStore.loadPreviousFilter();
      closeDialog && setIsOpen(false);
    },
    [filterStore],
  );

  useEffect(() => {
    filterStore.saveCurrentFilterAsPrevious();
  }, [filterStore]);

  const handleClearFilters = useCallback(() => {
    filterStore.clearAllFilters();
  }, [filterStore]);

  const activeFilterCount = filterStore.count;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        setIsOpen(false);
      }
      if (e.key === "Enter" && e.ctrlKey && isOpen) {
        handleApply();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, handleApply]);

  return (
    <div className="relative self-start" ref={popoverRef}>
      <Transition
        show={activeFilterCount > 0}
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <div
          className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-xs font-medium text-white shadow-sm"
          aria-label={`${activeFilterCount} active filters`}
        >
          {activeFilterCount}
        </div>
      </Transition>

      <Popover
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) handleCancel(false);
          setIsOpen(open);
        }}
      >
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="flex items-center gap-1.5 transition-all hover:bg-gray-100 focus:ring-2 focus:ring-primary/30 focus:ring-offset-2"
            aria-label="Filter options"
          >
            {activeFilterCount > 0 ? (
              <FilterIconSolid className="h-4 w-4 text-primary" />
            ) : (
              <FilterIconOutline className="h-4 w-4" />
            )}
            <span className="hidden sm:inline">Filter</span>
            {activeFilterCount > 0 && (
              <span className="sr-only">
                {activeFilterCount} active filters
              </span>
            )}
          </Button>
        </PopoverTrigger>

        <PopoverContent
          side={isMobile ? "bottom" : "right"}
          align="end"
          className="w-[calc(100vw-2rem)] max-w-md overflow-hidden rounded-lg border border-gray-200 shadow-lg focus:outline-none sm:w-96"
          sideOffset={8}
        >
          <Transition
            show={isOpen}
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <div>
              <div className="mb-5 flex items-center justify-between border-b border-gray-100 pb-3">
                <h3 className="text-sm font-semibold text-gray-900">
                  Filter options
                </h3>
                {activeFilterCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleClearFilters}
                    className="flex h-8 items-center gap-1 px-2 text-xs text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-900"
                  >
                    <XIcon className="h-3.5 w-3.5" />
                    <span>Clear all</span>
                  </Button>
                )}
              </div>

              <div className="max-h-[60vh] space-y-5 overflow-y-auto px-1 py-2">
                <DateRangeSelector />
                {numericPromptId && (
                  <PipelineSelector promptId={numericPromptId} />
                )}

                {filterStore.selectedEvaluation && numericPromptId && (
                  <DatasetSelector
                    pipelineId={filterStore.selectedEvaluation.id}
                    promptId={numericPromptId.toString()}
                  />
                )}

                <ReleaseLabelSelector
                  selectedLabel={filterStore.selectedLabel}
                  onLabelSelect={(label) => filterStore.setReleaseLabel(label)}
                  onLabelRemove={() => filterStore.clearReleaseLabel()}
                />

                {numericPromptId && (
                  <PromptVersionSelector
                    promptId={numericPromptId}
                    onValueChange={(value) => filterStore.setVersion(value)}
                    onRemove={() => filterStore.removeVersion()}
                    selectedVersion={filterStore.selectedVersion!}
                  />
                )}
              </div>

              <div className="mt-6 flex justify-end space-x-3 border-t border-gray-100 pt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleCancel()}
                  className="flex items-center gap-1 transition-colors hover:bg-gray-50"
                >
                  <XIcon className="h-3.5 w-3.5" />
                  <span>Cancel</span>
                </Button>
                <Button
                  size="sm"
                  onClick={handleApply}
                  className="flex items-center gap-1 transition-colors"
                >
                  <CheckIcon className="h-3.5 w-3.5" />
                  <span>Apply</span>
                </Button>
              </div>
            </div>
          </Transition>
        </PopoverContent>
      </Popover>
    </div>
  );
});

export default HistoryFilter;
